// UWB.tsx
import React from 'react';
import RadarModel3D from '../components/RadarModel3D';
import { Link } from 'react-router-dom';
import './UWB.css';

const UWB: React.FC = () => {
  const features = [
    {
      icon: '📍',
      title: 'Centimeter-Level Positioning',
      description: 'Precise spatial and directional tracking with up to 15 cm accuracy for critical applications.'
    },
    {
      icon: '🚪',
      title: 'Proximity Access Control',
      description: 'Detects approaching keys or smartphones to automatically lock/unlock doors seamlessly.'
    },
    {
      icon: '🚸',
      title: 'Child Presence Detection',
      description: 'Monitors rear seats for left-behind children or pets with high reliability and low false alarms.'
    },
    {
      icon: '🔄',
      title: 'Multi-Tag Tracking',
      description: 'Simultaneously tracks up to 16 tags (keys, phones, badges) for fleet or family management.'
    },
    {
      icon: '⚡',
      title: 'Low Interference',
      description: 'Operates at 8 GHz with extremely low power spectral density, no interference with other car electronics.'
    }
  ];

  const applications = [
    {
      title: 'Keyless Entry & Start',
      description: 'Passive entry with precise distance measurement – unlock only when driver approaches.'
    },
    {
      title: 'In-Cabin Monitoring',
      description: 'Detect occupant positions for airbag control, child alert, or gesture recognition.'
    },
    {
      title: 'Charging Alignment',
      description: 'Guide wireless charging pads or EV charging ports with cm-level precision.'
    },
    {
      title: 'Fleet Tracking',
      description: 'Track tools, assets or drivers inside logistics hubs and vehicles.'
    }
  ];

  return (
    <div className="product-page-roas uwb-page">
      {/* HERO — identical structure to ROAS */}
      <section
        className="product-hero-roas"
        style={{
          gap: '1rem',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: "url('/background/sensors_background.png')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.12,
            zIndex: 0,
            pointerEvents: 'none'
          }}
        ></div>
        <div className="product-hero-content-roas">
          <div className="product-badge-roas">Ultra-Wideband (UWB) 8 GHz</div>
          <h1>
            UWB - ultra wideband sensor <br />
          </h1>
          <p className="product-subtitle-roas">
            Centimeter-level spatial and directional tracking for automotive and industrial applications.<br />
            Enables secure keyless entry, in-cabin occupant detection, and asset tracking with ultra-low power.
          </p>
          <div className="info-buttons">
                <div className="product-cta">
                    <a href="/contact" className="btn-primary">
                    Get in Touch
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M7 13l5 5 5-5M7 6l5 5 5-5" />
                    </svg>
                    </a>
                </div>
                <div className="product-cta">
                    <a
                        href="#how-it-works"
                        className="btn-primary"
                        onClick={(e) => {
                        e.preventDefault();
                        const el = document.getElementById('how-it-works');
                        if (el) {
                            el.scrollIntoView({ behavior: 'smooth' });
                        }
                        }}
                    >
                        Learn more
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M7 13l5 5 5-5M7 6l5 5 5-5" />
                        </svg>
                    </a>
                    </div>
           </div>
        </div>

        {/* 3D Model — using same positioning props as ROAS */}
        <div className="product-hero-model-roas">
            <RadarModel3D 
                modelPath="/models/UWB.glb"
                cameraPosition={[0, -180, 0]}
                fov={45}
                modelCenter={[0, 0, 0]}
                modelScale={1.5}
                rotationAxis={'z'}
                rotationSpeed={0.008}
                rotationCenter={[0, 0, 0]}
            />
        </div>
      </section>

      {/* CORE FUNCTIONALITY — 6 cards */}
      <section className="product-section-roas">
        <div className="product-container-roas">
          <h2>Core Functionality</h2>
          <p className="section-description-roas">
            Next‑generation UWB technology delivering secure, precise, and low‑power ranging for any environment.
          </p>
          <div className="product-grid-roas">
            {features.map((feature, index) => (
              <div key={index} className="product-card-roas">
                <div className="product-icon-roas">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPLICATIONS — 4 cards */}
      <section className="product-section-roas product-section-roas-alt">
        <div className="product-container-roas">
          <h2>Applications</h2>
          <div className="product-grid-roas">
            {applications.map((app, index) => (
              <div key={index} className="product-app-card-roas">
                <h3>{app.title}</h3>
                <p>{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TECHNICAL SPECIFICATIONS */}
      <section className="product-section-roas" id="specs">
        <div className="product-container-roas">
          <h2>Technical Specifications</h2>
          <div className="specs-table-roas">
            <table>
              <tbody>
                <tr><td>Operating Frequency</td><td>6-8 GHz (UWB Channel 5, 6, 8, 9)</td></tr>
                <tr><td>Field of View (Az/EI)</td><td>120° / 120° (symmetrical)</td></tr>
                <tr><td>Minimum Distance</td><td>15 cm</td></tr>
                <tr><td>Maximum Distance (0° boresight)</td><td>50 meters</td></tr>
                <tr><td>Distance Accuracy</td><td>±15 cm (static), ±1 cm (dynamic)</td></tr>
                <tr><td>Power Supply</td><td>12 V DC (9 V – 16 V)</td></tr>
                <tr><td>Power Consumption</td><td>&lt; 150 mW (active), &lt; 10 µW (standby)</td></tr>
                <tr><td>Interface</td><td>CAN 2.0 / CAN-FD, UART</td></tr>
                <tr><td>Operating Temperature</td><td>-40°C … +85°C</td></tr>
                <tr><td>Dimensions</td><td>35 × 35 × 6 mm (without connector)</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UWB;